import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import EmailCapture from './EmailCapture';
import './App.css';

const MANIFEST_URL = '/podcast-content/_index.json';

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatDuration(seconds) {
  if (!seconds) return '';
  const s = Math.round(Number(seconds));
  if (!Number.isFinite(s) || s <= 0) return '';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  return `${m}:${String(ss).padStart(2, '0')}`;
}

const PodcastPage = () => {
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(MANIFEST_URL, { cache: 'no-cache' });
        if (!res.ok) throw new Error(`Manifest fetch failed (${res.status})`);
        const data = await res.json();
        if (!cancelled) setManifest(data);
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const episodes = (manifest?.episodes || []).slice().sort((a, b) => {
    return (b.publishedAt || '').localeCompare(a.publishedAt || '');
  });

  return (
    <div className="mp-page" style={{ background: '#fff', minHeight: 'calc(100vh - 60px)' }}>
      <section style={{ maxWidth: 960, margin: '0 auto', padding: '64px 24px 24px' }}>
        <span className="trust-badge trust-official" style={{ display: 'inline-block', marginBottom: 16 }}>
          Podcast
        </span>
        <h1 style={{ fontSize: '2.6rem', fontWeight: 800, color: '#111827', margin: '0 0 16px', lineHeight: 1.1 }}>
          {manifest?.showTitle || 'Made in mChatAI'}
        </h1>
        <p style={{ fontSize: '1.1rem', color: '#6b7280', lineHeight: 1.6, margin: '0 0 24px' }}>
          {manifest?.showSubtitle || ''}
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <a href="/podcast.xml" style={{
            display: 'inline-block', padding: '8px 16px', borderRadius: 8,
            background: '#f3f4f6', color: '#111827', textDecoration: 'none',
            fontSize: '0.9rem', fontWeight: 600, border: '1px solid #e5e7eb'
          }}>
            RSS feed
          </a>
          <a href="https://podcasts.apple.com" target="_blank" rel="noreferrer" style={{
            display: 'inline-block', padding: '8px 16px', borderRadius: 8,
            background: '#f3f4f6', color: '#111827', textDecoration: 'none',
            fontSize: '0.9rem', fontWeight: 600, border: '1px solid #e5e7eb'
          }}>
            Apple Podcasts
          </a>
          <a href="https://open.spotify.com" target="_blank" rel="noreferrer" style={{
            display: 'inline-block', padding: '8px 16px', borderRadius: 8,
            background: '#f3f4f6', color: '#111827', textDecoration: 'none',
            fontSize: '0.9rem', fontWeight: 600, border: '1px solid #e5e7eb'
          }}>
            Spotify
          </a>
        </div>
      </section>

      <section style={{ maxWidth: 960, margin: '0 auto', padding: '24px 24px 64px' }}>
        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
            padding: 16, borderRadius: 8, marginBottom: 24
          }}>
            Failed to load episodes: {error}
          </div>
        )}

        {!manifest && !error && (
          <p style={{ color: '#6b7280' }}>Loading episodes…</p>
        )}

        {manifest && episodes.length === 0 && (
          <div style={{
            background: '#f9fafb', border: '1px dashed #d1d5db',
            padding: 24, borderRadius: 12, color: '#6b7280', textAlign: 'center'
          }}>
            <p style={{ margin: 0, fontSize: '1rem' }}>
              Episode 1 is on the way. Subscribe in your podcast app to get it the moment it ships.
            </p>
          </div>
        )}

        <div style={{ display: 'grid', gap: 16 }}>
          {episodes.map((ep) => (
            <article
              key={ep.slug}
              style={{
                padding: 24, border: '1px solid #e5e7eb', borderRadius: 12, background: '#fff'
              }}
            >
              <div style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: 8 }}>
                {ep.episodeNumber && (
                  <span style={{ fontWeight: 600, color: '#111827' }}>
                    Ep {ep.episodeNumber}
                  </span>
                )}
                {ep.episodeNumber && <span> · </span>}
                <span>{formatDate(ep.publishedAt)}</span>
                {ep.durationSeconds && <span> · {formatDuration(ep.durationSeconds)}</span>}
              </div>
              <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>
                <Link to={`/podcast/${ep.slug}`} style={{ color: '#111827', textDecoration: 'none' }}>
                  {ep.title}
                </Link>
              </h2>
              <p style={{ color: '#4b5563', lineHeight: 1.6, margin: '0 0 12px' }}>
                {ep.summary}
              </p>
              {ep.audioURL && (
                <audio controls preload="none" style={{ width: '100%' }}>
                  <source src={ep.audioURL} type={ep.audioMimeType || 'audio/mpeg'} />
                  Your browser does not support the audio element.
                </audio>
              )}
              <div style={{ marginTop: 12 }}>
                <Link to={`/podcast/${ep.slug}`} style={{ color: '#3b82f6', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 600 }}>
                  Show notes →
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section style={{ maxWidth: 720, margin: '0 auto', padding: '24px 24px 96px' }}>
        <EmailCapture
          source="podcast-index"
          title="New episodes by email"
          subtitle="Prefer not to use a podcast app? Get each Friday’s episode in your inbox."
        />
      </section>
    </div>
  );
};

export default PodcastPage;
